import { config } from '../../utils/utils';
import store from '../../store';
import { toastHandler } from '../../utils/toastHandler';

export interface ProcessCreditExpirationResponse {
  success: boolean;
  message: string;
  expiredPackages: number;
  expiredCreditsFromCompany: number;
  expiredCreditsFromAgents: number;
  totalExpiredCredits: number;
  processedAt: string;
}

export interface ProcessAllResponse {
  success: boolean;
  message: string;
  results: {
    creditExpiration: ProcessCreditExpirationResponse | { error?: string };
    licenseExpiration: { success?: boolean; error?: string };
    companySuspension: { success?: boolean; error?: string };
  };
}

/**
 * Process credit expiration
 */
export const processCreditExpiration = async (): Promise<ProcessCreditExpirationResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/expiration-management/process-credit-expiration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to process credit expiration');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Check license expiration
 */
export const checkLicenseExpiration = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/expiration-management/check-license-expiration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check license expiration');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Suspend companies after grace period
 */
export const suspendCompaniesAfterGracePeriod = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/expiration-management/suspend-companies-after-grace-period`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to suspend companies after grace period');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Process all expiration tasks
 */
export const processAllExpirations = async (): Promise<ProcessAllResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/expiration-management/process-all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to process all expirations');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

